import React, { useState, useEffect, useContext } from 'react';
import {
  Box, CssBaseline, AppBar, Toolbar, Typography,
  TextField, Button, Card, CardContent, Grid,
  Alert, Snackbar, CircularProgress, Backdrop,
  Chip, TextareaAutosize
} from '@mui/material';
import { Save as SaveIcon, Delete as DeleteIcon } from '@mui/icons-material';
import axios from 'axios';
import SideButton from './SideButton';
import Header from './Header';
import AuthContext from '../../auth/Auth-context';

const drawerWidth = 270;
const sliderWidth = 42.1;

const Add_Keyword = () => {
  const authCtx = useContext(AuthContext);
  const [keywordInput, setKeywordInput] = useState('');
  const [keywordChips, setKeywordChips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [existingKeywords, setExistingKeywords] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);
  const [saveProgress, setSaveProgress] = useState({ current: 0, total: 0, isSaving: false });

  useEffect(() => {
    fetchExistingKeywords();
  }, []);

  const fetchExistingKeywords = async () => {
    try {
      const response = await axios.get('https://server.taskticks.com/api/items/keywords');
      const keywords = response.data.map(item => ({
        id: item._id,
        keyword: item.keyword,
        createdAt: new Date(item.createdAt),
        createdBy: item.createdBy
      }));
      setExistingKeywords(keywords);
    } catch (error) {
      console.error('Error fetching keywords:', error);
      showSnackbar('Error fetching existing keywords', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const parseKeywords = (input) => {
    // Split by comma or newline, but preserve spaces within keywords
    const keywords = input
      .split(/[,\n]+/)
      .map(keyword => keyword.trim())
      .filter(keyword => keyword.length > 0);
    
    // Remove duplicates (case-insensitive) and normalize spaces
    const uniqueKeywords = [];
    const seen = new Set();
    
    keywords.forEach(keyword => {
      // Normalize multiple spaces to single space
      const normalizedKeyword = keyword.replace(/\s+/g, ' ').trim();
      const lowerKeyword = normalizedKeyword.toLowerCase();
      
      if (!seen.has(lowerKeyword) && normalizedKeyword.length > 0) {
        seen.add(lowerKeyword);
        uniqueKeywords.push(normalizedKeyword);
      }
    });
    
    return uniqueKeywords;
  };

  const handleKeywordSubmit = () => {
    const newKeywords = parseKeywords(keywordInput);
    
    if (newKeywords.length === 0) {
      showSnackbar('Please enter at least one keyword', 'warning');
      return;
    }

    // Add new keywords to existing chips, avoiding duplicates
    const existingChipKeywords = keywordChips.map(chip => chip.keyword.toLowerCase());
    const uniqueNewKeywords = newKeywords.filter(keyword => 
      !existingChipKeywords.includes(keyword.toLowerCase())
    );

    const newChips = uniqueNewKeywords.map((keyword, index) => ({
      id: Date.now() + index,
      keyword: keyword
    }));

    setKeywordChips([...keywordChips, ...newChips]);
    setKeywordInput('');
    
    if (uniqueNewKeywords.length < newKeywords.length) {
      showSnackbar(`Added ${uniqueNewKeywords.length} keywords. ${newKeywords.length - uniqueNewKeywords.length} duplicates were skipped.`, 'info');
    } else {
      showSnackbar(`Added ${newKeywords.length} keywords`, 'success');
    }
  };

  const removeKeywordChip = (chipId) => {
    setKeywordChips(keywordChips.filter(chip => chip.id !== chipId));
  };

  // Filter existing keywords based on search term
  const filteredKeywords = existingKeywords.filter(keyword =>
    keyword.keyword.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const toggleBulkDeleteMode = () => {
    setBulkDeleteMode(!bulkDeleteMode);
    setSelectedKeywords([]);
  };

  const toggleKeywordSelection = (keywordId) => {
    setSelectedKeywords(prev => 
      prev.includes(keywordId) 
        ? prev.filter(id => id !== keywordId)
        : [...prev, keywordId]
    );
  };

  const selectAllKeywords = () => {
    setSelectedKeywords(filteredKeywords.map(k => k.id));
  };

  const clearSelection = () => {
    setSelectedKeywords([]);
  };

  const handleBulkDelete = async () => {
    if (selectedKeywords.length === 0) {
      showSnackbar('Please select keywords to delete', 'warning');
      return;
    }

    if (window.confirm(`Are you sure you want to delete ${selectedKeywords.length} keyword(s)?`)) {
      setLoading(true);
      try {
        await axios.delete('https://server.taskticks.com/api/items/keywords', {
          data: { ids: selectedKeywords }
        });
        showSnackbar(`Successfully deleted ${selectedKeywords.length} keyword(s)`, 'success');
        setSelectedKeywords([]);
        setBulkDeleteMode(false);
        fetchExistingKeywords();
      } catch (error) {
        console.error('Error deleting keywords:', error);
        showSnackbar('Error deleting keywords', 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSave = async () => {
    if (keywordChips.length === 0) {
      showSnackbar('Please add at least one keyword', 'warning');
      return;
    }

    setLoading(true);
    setSaveProgress({ current: 0, total: keywordChips.length, isSaving: true });
    
    try {
      let savedCount = 0;
      let skippedCount = 0;
      let currentIndex = 0;

      for (const chip of keywordChips) {
        currentIndex++;
        setSaveProgress({ current: currentIndex, total: keywordChips.length, isSaving: true });
        
        // Check if keyword already exists in database
        const exists = existingKeywords.some(ek => 
          ek.keyword.toLowerCase() === chip.keyword.toLowerCase()
        );
        
        if (exists) {
          skippedCount++;
          continue;
        }

        await axios.post('https://server.taskticks.com/api/items/keywords', {
          keyword: chip.keyword.trim(),
          createdAt: new Date(),
          createdBy: authCtx.email || 'Unknown'
        });
        savedCount++;
        
        // Small delay to make progress visible
        await new Promise(resolve => setTimeout(resolve, 200));
      }
      
      if (savedCount > 0) {
        showSnackbar(`Successfully saved ${savedCount} keywords!${skippedCount > 0 ? ` ${skippedCount} keywords already existed.` : ''}`, 'success');
        setKeywordChips([]);
        fetchExistingKeywords();
      } else {
        showSnackbar('All keywords already exist in the database', 'warning');
      }
    } catch (error) {
      console.error('Error saving keywords:', error);
      showSnackbar('Error saving keywords', 'error');
    } finally {
      setLoading(false);
      setSaveProgress({ current: 0, total: 0, isSaving: false });
    }
  };

  const handleDeleteKeyword = async (keywordId) => {
    if (window.confirm('Are you sure you want to delete this keyword?')) {
      try {
        await axios.delete(`https://server.taskticks.com/api/items/keywords/${keywordId}`);
        showSnackbar('Keyword deleted successfully', 'success');
        fetchExistingKeywords();
      } catch (error) {
        console.error('Error deleting keyword:', error);
        showSnackbar('Error deleting keyword', 'error');
      }
    }
  };

        return (
    <>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {loading && (
        <Backdrop sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }} open={loading}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress color="inherit" size={60} />
            {saveProgress.isSaving && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
                  Saving Keywords
                </Typography>
                <Typography variant="body1" sx={{ color: 'white', mb: 2 }}>
                  {saveProgress.current} of {saveProgress.total} keywords processed
                </Typography>
                <Box sx={{ width: 300, bgcolor: 'rgba(255,255,255,0.3)', borderRadius: 1, overflow: 'hidden' }}>
                  <Box 
                    sx={{ 
                      width: `${(saveProgress.current / saveProgress.total) * 100}%`,
                      height: 8,
                      bgcolor: 'white',
                      transition: 'width 0.3s ease'
                    }} 
                  />
                </Box>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', mt: 1, display: 'block' }}>
                  {Math.round((saveProgress.current / saveProgress.total) * 100)}% complete
                </Typography>
              </Box>
            )}
          </Box>
        </Backdrop>
      )}

      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <AppBar
          position="fixed"
          sx={{
            width: { xs: `calc(100% - ${sliderWidth}px)`, md: `calc(100% - ${drawerWidth}px)` },
            ml: { xs: `${sliderWidth}px`, md: `${drawerWidth}px` }
          }}
        >
          <Toolbar sx={{ background: '#fff' }}>
            <Typography variant="h6" sx={{ color: '#333', fontWeight: 'bold', flexGrow: 1 }}>
              Analytics Tool - Add Keywords
            </Typography>
            <Header />
          </Toolbar>
        </AppBar>

        <SideButton />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            bgcolor: 'background.default',
            p: 3,
            px: { xs: 3, md: 5 },
            zIndex: 1,
            overflowX: 'hidden'
          }}
        >
          <Toolbar />
          
          <Box className="container">
            <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold', color: '#1976d2' }}>
              Add Keywords
            </Typography>

            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Add New Keywords
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Paste multiple keywords separated by commas or new lines. Multi-word keywords like "hello world" are preserved as single keywords.
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <TextareaAutosize
                    minRows={4}
                    maxRows={8}
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    placeholder="Enter keywords separated by commas or new lines...&#10;Example:&#10;hello world, javascript, node.js&#10;or&#10;hello world&#10;javascript&#10;node.js&#10;&#10;Note: 'hello world' stays as one keyword"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #c4c4c4',
                      borderRadius: '4px',
                      fontSize: '16px',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      outline: 'none'
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.ctrlKey) {
                        e.preventDefault();
                        handleKeywordSubmit();
                      }
                    }}
                  />
                </Box>

                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                  <Button
                    variant="contained"
                    onClick={handleKeywordSubmit}
                    disabled={!keywordInput.trim()}
                  >
                    Parse Keywords
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                    Tip: Press Ctrl+Enter to parse keywords
                  </Typography>
                </Box>

                {keywordChips.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>
                      Keywords to Save ({keywordChips.length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {keywordChips.map((chip) => (
                        <Chip
                          key={chip.id}
                          label={chip.keyword}
                          onDelete={() => removeKeywordChip(chip.id)}
                          deleteIcon={<DeleteIcon />}
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={loading || keywordChips.length === 0}
                  >
                    {saveProgress.isSaving 
                      ? `Saving... (${saveProgress.current}/${saveProgress.total})` 
                      : `Save Keywords (${keywordChips.length})`
                    }
                  </Button>
                  {keywordChips.length > 0 && (
                    <Button
                      variant="outlined"
                      onClick={() => setKeywordChips([])}
                    >
                      Clear All
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Existing Keywords ({searchTerm ? `${filteredKeywords.length} of ${existingKeywords.length}` : existingKeywords.length})
                  </Typography>
                  {existingKeywords.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TextField
                        size="small"
                        placeholder="Search keywords..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        sx={{ width: '250px' }}
                        InputProps={{
                          startAdornment: (
                            <Box sx={{ mr: 1, color: 'text.secondary' }}>
                              🔍
                            </Box>
                          )
                        }}
                      />
                      {searchTerm && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setSearchTerm('')}
                          sx={{ minWidth: 'auto', px: 1 }}
                        >
                          Clear
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant={bulkDeleteMode ? "contained" : "outlined"}
                        color={bulkDeleteMode ? "error" : "primary"}
                        onClick={toggleBulkDeleteMode}
                        sx={{ minWidth: 'auto', px: 2 }}
                      >
                        {bulkDeleteMode ? 'Cancel' : 'Bulk Delete'}
                      </Button>
                    </Box>
                  )}
                </Box>

                {bulkDeleteMode && filteredKeywords.length > 0 && (
                  <Box sx={{ mb: 2, p: 2, bgcolor: 'rgba(244, 67, 54, 0.1)', borderRadius: 1, border: '1px solid rgba(244, 67, 54, 0.3)' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="error">
                        {selectedKeywords.length > 0 
                          ? `${selectedKeywords.length} keyword(s) selected` 
                          : 'Select keywords to delete'
                        }
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={selectAllKeywords}
                          disabled={selectedKeywords.length === filteredKeywords.length}
                        >
                          Select All
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={clearSelection}
                          disabled={selectedKeywords.length === 0}
                        >
                          Clear Selection
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="error"
                          onClick={handleBulkDelete}
                          disabled={selectedKeywords.length === 0}
                        >
                          Delete Selected ({selectedKeywords.length})
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                )}
                
                {existingKeywords.length > 0 ? (
                  <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                    {filteredKeywords.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {filteredKeywords.map((keyword) => {
                          const isSelected = selectedKeywords.includes(keyword.id);
                          return (
                            <Chip
                              key={keyword.id}
                              label={keyword.keyword}
                              onDelete={bulkDeleteMode ? undefined : () => handleDeleteKeyword(keyword.id)}
                              onClick={bulkDeleteMode ? () => toggleKeywordSelection(keyword.id) : undefined}
                              deleteIcon={bulkDeleteMode ? undefined : <DeleteIcon />}
                              color={bulkDeleteMode ? (isSelected ? "error" : "secondary") : "secondary"}
                              variant={bulkDeleteMode ? (isSelected ? "filled" : "outlined") : "outlined"}
                              sx={{
                                cursor: bulkDeleteMode ? 'pointer' : 'default',
                                opacity: bulkDeleteMode && !isSelected ? 0.6 : 1,
                                transform: bulkDeleteMode && isSelected ? 'scale(1.05)' : 'scale(1)',
                                transition: 'all 0.2s ease',
                                '& .MuiChip-label': {
                                  maxWidth: '200px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }
                              }}
                              title={bulkDeleteMode 
                                ? (isSelected ? 'Click to deselect' : 'Click to select for deletion')
                                : `Added by: ${keyword.createdBy} • ${keyword.createdAt?.toLocaleDateString() || 'Unknown date'}`
                              }
                            />
                          );
                        })}
                      </Box>
                    ) : (
                      <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                        No keywords found matching "{searchTerm}"
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography color="text.secondary">
                    No keywords found. Add some keywords above to get started.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>
    </>
  );
};

    export default Add_Keyword;


 